import type { UserStatusType } from 'aws-sdk/clients/cognitoidentityserviceprovider';
import { jwtDecode } from 'jwt-decode';
import { AuthRole } from '../constants/shared/auth-roles';
import { getSpoofedGroups, SpoofedGroup } from '../utils/spoofed-groups';
import { IdentityProvider, IdpToProviderName, ProviderNameToIdp } from './IdentityProvider';
import { Nullable } from './common';

/**
 * Expected type of a user's parsed ID token.
 */
export interface IdTokenParsed {
  readonly email?: string;
  readonly given_name?: string;
  readonly family_name?: string;
  readonly name?: string;
  readonly 'cognito:username': string;
  readonly 'cognito:groups': string[];
  readonly identities?: [
    {
      readonly providerName?: string;
    }
  ];
  readonly public_provider_name?: string;
}

/**
 * Properties used to create a User.
 */
export interface UserProps {
  readonly name: string;
  readonly email: string;
  readonly vibeId: string;
  readonly identityProvider: Nullable<IdentityProvider>;
  readonly groups: string[];
  readonly legacyLogin?: string;
  readonly nickname?: string;
  readonly preferredLanguageCode?: string;
}

export class User {
  public readonly name: string;
  public readonly email: string;
  public readonly vibeId: string;
  public readonly provider: Nullable<IdentityProvider>;
  public readonly legacyLogin?: string;
  public readonly nickname?: string;
  public readonly preferredLanguageCode?: string;
  private groups: string[];

  /**
   * Create a User from the given properties.
   *
   * @param userProps - Properties of the user.
   */
  public constructor(userProps: UserProps) {
    this.name = userProps.name;
    this.email = userProps.email;
    this.vibeId = userProps.vibeId;
    this.provider = userProps.identityProvider;
    this.groups = userProps.groups;
    this.legacyLogin = userProps.legacyLogin;
    this.nickname = userProps.nickname;
    this.preferredLanguageCode = userProps.preferredLanguageCode;
  }

  /**
   * Create an exact copy of this User.
   */
  public clone(overrides: Partial<UserProps> = {}): User {
    return new User({
      email: this.email,
      name: this.name,
      vibeId: this.vibeId,
      identityProvider: this.provider,
      groups: this.groups,
      legacyLogin: this.legacyLogin,
      nickname: this.nickname,
      preferredLanguageCode: this.preferredLanguageCode,
      ...overrides,
    });
  }

  /**
   * Create a user from the given id token.
   */
  public static fromIdToken(idToken: string): User {
    const jwt = jwtDecode<any>(idToken);

    const email = (
      jwt.email ||
      (jwt?.identities?.[0]?.providerName === 'ScaleTestIdp' && jwt.identities[0].userId) || // ScaleTestIdp used in load testing does not send email claim, we extract email from userId
      ''
    ).toLowerCase();

    const vibeId = jwt.vibe_user_id;
    const firstName = jwt.given_name;
    const lastName = jwt.family_name;
    const name = jwt.name;
    const username = jwt['cognito:username'];
    const legacyLogin = `AWS-Skill-Builder_${jwt.sub as string}`;
    const providerName = ProviderNameToIdp[jwt.public_provider_name] ?? null;

    return new User({
      email,
      vibeId,
      name: firstName && lastName ? firstName + ' ' + lastName : name || username,
      identityProvider: providerName,
      groups: jwt['cognito:groups'],
      legacyLogin,
    });
  }

  get isAmazonian() {
    return this.provider === IdentityProvider.AmazonFederate;
  }

  get isTCUser() {
    return this.isMatchingIdp(IdentityProvider.TC);
  }

  get isEventAdmin() {
    return this.memberOfGroup(AuthRole.EVENT_SUPPORT);
  }

  get isCampaignAdmin() {
    return this.memberOfGroup(AuthRole.EVENT_SUPPORT);
  }

  get isChallengeAdmin() {
    return this.memberOfGroup(AuthRole.CHALLENGE_SUPPORT);
  }

  get isChallengeBuilder() {
    return this.memberOfGroup(AuthRole.CHALLENGE_BUILDER);
  }

  get isSuperAdmin() {
    return this.memberOfGroup(AuthRole.JAM_ADMIN);
  }

  get isBarRaiser() {
    return this.memberOfGroup(AuthRole.CHALLENGE_REVIEWER);
  }

  /**
   * Can view and edit challenge translations. This role grants nothing else --
   * it does not imply challenge authoring, so do not use it to gate any
   * challenge mutation other than translations.
   */
  get isChallengeTranslator() {
    return this.memberOfGroup(AuthRole.CHALLENGE_TRANSLATOR);
  }

  get isSubscribedUser() {
    return this.memberOfGroup(AuthRole.SUBSCRIBED_USER);
  }

  get isSbtsLearner() {
    return this.memberOfGroup(AuthRole.SBTS_LEARNER);
  }

  get hasBasicAccess() {
    return this.memberOfGroup(AuthRole.GUEST_USER);
  }

  get canScheduleAnEvent() {
    return this.isSubscribedUser || this.isAmazonian || this.isSuperAdmin || this.isSbtsLearner;
  }

  get isOnlyBasicUser() {
    // has access to only a single role
    //
    // NOTE: isChallengeTranslator is deliberately excluded from this list. This
    // getter gates elevated challenge/event UI (settings, stats, next steps,
    // issues, learning plan), none of which a translator is authorized for.
    // A translator reaches the translation view through
    // ChallengeUtils.canEditTranslations instead.
    return (
      this.hasBasicAccess &&
      !this.isAmazonian &&
      !this.isTCUser &&
      !this.isEventAdmin &&
      !this.isCampaignAdmin &&
      !this.isChallengeAdmin &&
      !this.isChallengeBuilder &&
      !this.isSuperAdmin &&
      !this.isBarRaiser &&
      !this.isSubscribedUser &&
      !this.isSbtsLearner
    );
  }

  /**
   * Get the identity provider for the current user.
   * Returning null means a username/password user.
   * If the role-spoofing feature is being used, then the spoofed idp is used here.
   */
  get idp(): IdentityProvider | null {
    return Object.values(IdentityProvider).find((idp: IdentityProvider) => this.isMatchingIdp(idp)) || null;
  }

  get combinedGroups(): SpoofedGroup[] | string[] {
    const spoofedGroups: string[] = getSpoofedGroups();
    const groups: SpoofedGroup[] | string[] = spoofedGroups.length > 0 ? [...spoofedGroups] : [...this.groups];
    return groups;
  }

  public memberOfGroup(groupName: string): boolean {
    const spoofedGroups: string[] = getSpoofedGroups();
    const groups: SpoofedGroup[] | string[] = spoofedGroups.length > 0 ? [...spoofedGroups] : [...this.groups];
    return groups.includes(groupName);
  }

  public isMatchingIdp(idp: IdentityProvider): boolean {
    const providerName = IdpToProviderName[idp];
    if (getSpoofedGroups().length > 0) {
      return this.memberOfGroup(providerName);
    }
    return this.provider === idp;
  }
}

export const DEFAULT_STATUS = 'EXTERNAL_PROVIDER';

export interface CognitoUser {
  email: string;
  login: string;
  roles: string[];
  providerName: string;
  cognitoUsername: string;
  emailVerified: boolean;
  status: UserStatusType;
  enabled: boolean;
  vibeId?: string;
  firstName?: string;
  givenName?: string;
  lastName?: string;
  familyName?: string;
  publicProviderName?: string;
  legacyLogin?: string;
}
